import { useCallback, useContext, useMemo } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { DataContext } from '../context/DataContext';
import { WHISPERBI_SECURITY_API } from '../env';

const API_BASE_URL = WHISPERBI_SECURITY_API;
const ROLES_URL = `${API_BASE_URL}/roles`;
const PERMISSIONS_CATALOG_URL = `${API_BASE_URL}/permissions/catalog`;
const PERMISSIONS_ME_URL = `${API_BASE_URL}/permissions/me`;

async function getIdToken() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    return tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('SecurityRolesService.getIdToken: unable to fetch auth session', error);
    return null;
  }
}

async function request(url, options = {}, { requireAuth = true } = {}) {
  const init = { ...options };
  const headers = new Headers(init.headers || {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (requireAuth) {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se encontró un token de autenticación para realizar la operación solicitada.');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  init.headers = headers;
  init.mode = init.mode || 'cors';
  init.cache = init.cache || 'no-cache';

  let response;
  try {
    response = await fetch(url, init);
  } catch (networkError) {
    console.error('SecurityRolesService.request: network error', networkError);
    throw new Error('No se pudo conectar con el servicio de roles y permisos. Verifica tu conexión o intenta más tarde.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      const backendMessage = data?.message || data?.error || data?.errorMessage;
      if (backendMessage) {
        errorMessage = backendMessage;
      }
    } catch {
      const text = await response.text().catch(() => '');
      if (text) {
        errorMessage = text;
      }
    }
    throw new Error(errorMessage || 'Error inesperado al comunicar con el servicio de roles y permisos.');
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

export async function getPermissionsCatalog() {
  return request(PERMISSIONS_CATALOG_URL, { method: 'GET' });
}

export async function getMyPermissions() {
  return request(PERMISSIONS_ME_URL, { method: 'GET' });
}

export async function listRoles() {
  return request(ROLES_URL, { method: 'GET' });
}

// Un Role es ahora una composición de Políticas (policy_ids, referencias a
// SecurityPoliciesService) en vez de una lista plana de permission keys
// embebida -- ver functions/security-roles/app.py::create_role/update_role.
export async function createRole({ name, description, policyIds } = {}) {
  if (!name || !name.trim()) {
    throw new Error('El nombre del rol es obligatorio.');
  }

  const payload = {
    name: name.trim(),
    description: description || '',
    policy_ids: Array.isArray(policyIds) ? policyIds : []
  };

  return request(ROLES_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function updateRole(roleId, { name, description, policyIds } = {}) {
  if (!roleId) {
    throw new Error('Se requiere el identificador del rol para actualizarlo.');
  }

  const payload = {
    name: name ? name.trim() : name,
    description,
    policy_ids: Array.isArray(policyIds) ? policyIds : []
  };

  const url = `${ROLES_URL}/${encodeURIComponent(roleId)}`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
}

export async function deleteRole(roleId) {
  if (!roleId) {
    throw new Error('Se requiere el identificador del rol para eliminarlo.');
  }

  const url = `${ROLES_URL}/${encodeURIComponent(roleId)}`;
  return request(url, { method: 'DELETE' });
}

function ensureRoleManagerAccess(isPlatformRoot, isTenantAdmin) {
  if (!isPlatformRoot && !isTenantAdmin) {
    throw new Error('Solo administradores de la cuenta pueden realizar esta operación.');
  }
}

export const useSecurityRolesService = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const guard = useCallback(() => {
    ensureRoleManagerAccess(isPlatformRoot, isTenantAdmin);
  }, [isPlatformRoot, isTenantAdmin]);

  const guardedActions = useMemo(() => ({
    createRole: async (payload) => {
      guard();
      return createRole(payload);
    },
    updateRole: async (roleId, payload) => {
      guard();
      return updateRole(roleId, payload);
    },
    deleteRole: async (roleId) => {
      guard();
      return deleteRole(roleId);
    }
  }), [guard]);

  return {
    ...guardedActions,
    // Solo lectura: cualquier usuario autenticado puede consultarlas.
    listRoles,
    getPermissionsCatalog,
    getMyPermissions
  };
};

const SecurityRolesService = {
  listRoles,
  createRole,
  updateRole,
  deleteRole,
  getPermissionsCatalog,
  getMyPermissions,
  useSecurityRolesService
};

export default SecurityRolesService;
